import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Car, Check, Layers, ShieldCheck, Sparkles, Upload, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PlatformTile } from "@/components/PlatformTile";

const HERO_IMG = "https://images.unsplash.com/photo-1528109966604-5a6a4a964e8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Feature = ({ icon: Icon, title, text, testid }) => (
  <div className="card-surface card-hover p-6" data-testid={testid}>
    <span className="h-10 w-10 rounded-xl bg-primary/12 text-primary grid place-items-center mb-4"><Icon size={20} /></span>
    <h3 className="text-lg mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
  </div>
);

export default function Home() {
  const { user, login, loading } = useAuth();
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    if (user) api.get("/history").then((r) => setRecent(r.data.slice(0, 3))).catch(() => {});
  }, [user]);

  return (
    <div className="space-y-16">
      <section className="grid lg:grid-cols-12 gap-8 items-center rise">
        <div className="lg:col-span-7 space-y-6">
          <span className="overline-label">ReelFlo.io · video repurposing studio</span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95]">
            Go viral<br /><span className="text-primary">fifty times</span> over.
          </h1>
          <p className="text-base text-muted-foreground max-w-xl leading-relaxed">
            ReelFlo cuts, reframes, re-paces and captions your source video into 50 distinct clips, rendered server-side with real ffmpeg jobs. Preview, edit, approve, then share to TikTok, Reels, Shorts and more.
          </p>
          <div className="flex flex-wrap gap-3">
            {user ? (
              <Button asChild className="btn-pill h-12 px-6" data-testid="hero-open-editor">
                <Link to="/editor"><Upload size={16} className="mr-2" /> Open the editor <ArrowRight size={16} className="ml-2" /></Link>
              </Button>
            ) : (
              <Button onClick={login} disabled={loading} className="btn-pill h-12 px-6" data-testid="google-signin-button">
                <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-4.1-13.6-9.9l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google
              </Button>
            )}
            <Button variant="outline" asChild className="btn-pill h-12 px-6" data-testid="hero-membership-link">
              <Link to={user ? "/membership" : "#plans"}>See plans</Link>
            </Button>
          </div>
          <div className="flex flex-wrap gap-6 pt-2 text-sm text-muted-foreground font-mono">
            <span>50 variations / batch</span><span>·</span><span>9:16 · 1:1 · 16:9</span><span>·</span><span>Whisper captions</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="glow-blob w-72 h-72 -top-10 -right-10 opacity-90" />
          <div className="card-surface relative p-5 space-y-4" data-testid="hero-mock">
            <div className="flex items-center justify-between text-sm"><span className="font-semibold">New Post</span><span className="text-xs text-muted-foreground">Batch · 50 clips</span></div>
            <div className="rounded-xl bg-black aspect-[16/10] overflow-hidden relative">
              <img src={HERO_IMG} alt="Creator studio" className="absolute inset-0 w-full h-full object-cover opacity-80" />
              <div className="absolute inset-x-6 bottom-4 flex justify-between gap-2">
                {["9:16", "1:1", "16:9"].map((r) => <span key={r} className="rounded-lg bg-black/70 border border-white/10 text-white px-2.5 py-1 text-[10px] font-mono">{r}</span>)}
              </div>
            </div>
            <div className="flex items-center justify-between"><span className="section-label">Share to</span><span className="text-xs text-muted-foreground">All selected</span></div>
            <div className="grid grid-cols-2 gap-2">
              {[["tiktok", "TikTok"], ["reels", "Reels"], ["shorts", "Shorts"], ["x", "X"]].map(([id, label]) => (
                <div key={id} className="platform-card platform-card-active"><PlatformTile id={id} size={26} /><span className="flex-1 text-sm font-semibold">{label}</span><span className="h-5 w-5 rounded-full bg-primary text-primary-foreground grid place-items-center"><Check size={12} strokeWidth={3} /></span></div>
              ))}
            </div>
            <div className="btn-pill bg-primary text-primary-foreground text-center py-2.5 text-sm">Share to 4 platforms</div>
          </div>
          <div className="absolute -top-4 -left-3 ai-badge h-12 w-12 text-base">AI</div>
        </div>
      </section>

      {user && (
        <section className="rise" data-testid="car-edits-cta">
          <Link to="/car-edits" className="card-surface card-hover p-6 flex flex-wrap items-center gap-5 overflow-hidden relative">
            <span className="absolute inset-y-0 left-0 w-1.5 bg-primary" />
            <span className="h-14 w-14 shrink-0 rounded-2xl bg-primary/12 text-primary grid place-items-center"><Car size={28} /></span>
            <div className="flex-1 min-w-[200px]">
              <span className="overline-label">New · Car Edits</span>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight mt-1">Turn raw car clips into one punchy, music-synced reel</h2>
              <p className="text-sm text-muted-foreground mt-1">Upload a handful of clips, tag them, pick a style & beat, and tap create.</p>
            </div>
            <span className="btn-pill bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold flex items-center gap-2">Open Car Edits <ArrowRight size={15} /></span>
          </Link>
        </section>
      )}

      {user && recent.length > 0 && (
        <section className="space-y-4 rise" data-testid="recent-batches">
          <div className="flex items-center justify-between"><span className="overline-label">Recent batches</span><Link to="/history" className="text-sm text-primary hover:underline">View all</Link></div>
          <div className="grid sm:grid-cols-3 gap-4">
            {recent.map((b) => (
              <Link key={b.id} to={`/editor/${b.project_id}?batch=${b.id}`} className="card-surface card-hover p-5 block" data-testid={`recent-batch-${b.id}`}>
                <p className="font-semibold truncate">{b.project_title}</p>
                <p className="text-xs text-muted-foreground font-mono mt-1">{b.count} variations · {b.status.replace(/_/g, " ")}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" id="plans">
        <Feature icon={Wand2} title="Real renders" text="Every variation is an ffmpeg job: different moment, framing, pacing, hook overlay and burned-in captions." testid="feature-renders" />
        <Feature icon={Layers} title="Batch progress" text="Watch 50 jobs move through queued, processing, rendered or failed, and retry any single failure." testid="feature-progress" />
        <Feature icon={Sparkles} title="AI hooks & captions" text="Whisper transcription feeds distinct opening hooks and platform captions per clip." testid="feature-ai" />
        <Feature icon={ShieldCheck} title="Locked-down media" text="Restricted storage paths, expiring signed links, persistent accounts and abuse limits." testid="feature-security" />
      </section>
    </div>
  );
}
